import { join } from "node:path";
// Wrapper around simple-git for the operations Avatar uses repeatedly:
// repo detection, submodule add/update, status, log, tag listing.
// Centralised so error formatting and cwd handling stays consistent.
import { type SimpleGit, simpleGit } from "simple-git";
import { pathExists } from "./filesystem-helpers.js";

export function git(cwd: string = process.cwd()): SimpleGit {
  return simpleGit({ baseDir: cwd, binary: "git" });
}

export async function isGitRepo(cwd: string = process.cwd()): Promise<boolean> {
  return await pathExists(join(cwd, ".git"));
}

export async function currentBranch(cwd: string = process.cwd()): Promise<string> {
  const result = await git(cwd).revparse(["--abbrev-ref", "HEAD"]);
  return result.trim();
}

export async function addSubmodule(
  repoUrl: string,
  destPath: string,
  cwd: string = process.cwd(),
): Promise<void> {
  await git(cwd).subModule(["add", repoUrl, destPath]);
}

// Checkout a tag inside a submodule. Used after `addSubmodule` to pin to a
// specific team-ai-pack release.
export async function checkoutTagInSubmodule(
  submodulePath: string,
  tag: string,
  cwd: string = process.cwd(),
): Promise<void> {
  const submoduleCwd = join(cwd, submodulePath);
  await git(submoduleCwd).fetch(["--tags"]);
  await git(submoduleCwd).checkout(tag);
}

// v1.8.0: Checkout HEAD của branch (default main) thay vì tag.
// Dùng cho `avatar sync --latest` — pull commits mới ngay khi pack team push,
// không cần đợi tag SemVer mới. Bleeding-edge mode cho member dev.
//
// Flow:
//   1. fetch origin (lấy commits mới + tags)
//   2. checkout <branch> (di chuyển HEAD tới branch local, tạo nếu thiếu)
//   3. reset --hard origin/<branch> (force align với remote, xóa local divergence)
//
// reset --hard cần thiết vì submodule có thể đang detached HEAD (sau checkout tag).
// Pack thường KHÔNG có local commits → reset --hard safe.
export async function checkoutBranchHeadInSubmodule(
  submodulePath: string,
  branch: string,
  cwd: string = process.cwd(),
): Promise<void> {
  const submoduleCwd = join(cwd, submodulePath);
  await git(submoduleCwd).fetch(["origin"]);
  // Tạo / switch local branch tracking remote.
  await git(submoduleCwd).checkout(["-B", branch, `origin/${branch}`]);
}

export async function listTags(cwd: string = process.cwd()): Promise<string[]> {
  const result = await git(cwd).tags();
  return result.all;
}

// v1.7.1: Return tag tại HEAD nếu có (semantics khác latestTag).
// Dùng cho avatar status / readPinnedPackVersion — báo user đang ở version nào.
export async function tagAtHead(cwd: string = process.cwd()): Promise<string | null> {
  try {
    const result = await git(cwd).raw(["describe", "--tags", "--exact-match", "HEAD"]);
    return result.trim() || null;
  } catch {
    // HEAD không trùng tag nào → return null, caller fallback dùng SHA.
    return null;
  }
}

export async function currentCommitSha(cwd: string = process.cwd()): Promise<string> {
  const result = await git(cwd).revparse(["HEAD"]);
  return result.trim();
}

export async function workingTreeIsDirty(cwd: string = process.cwd()): Promise<boolean> {
  const status = await git(cwd).status();
  return !status.isClean();
}
